import styles from "./standard.module.css"

import IconRatingStarEmpty from "@shared/components/icons/RatingStarEmpty"
import IconRatingStarFilledIn from "@shared/components/icons/RatingStarFilledIn"
import { useNotifier } from "@shared/components/providers/notifier-context"
import { cx } from "@shared/components/untitledui/util/cx"
import { getContainingCardId } from "@shared/unified-tiptap/util/card"
import { tracked } from "@supergrain/kernel/react"
import type { ExtensionStandardAttrs } from "@tiptap-content/extensions/standard/standard-content-extension"
import type { Transaction } from "@tiptap/pm/state"
import { NodeViewWrapper, type ReactNodeViewProps } from "@tiptap/react"
import { Copy05, Trash01 } from "@untitledui/icons"
import DOMPurify from "dompurify"
import { useMemo, type DragEvent, type MouseEvent } from "react"
import { Button as AriaButton } from "react-aria-components"
import { standardIdOfNode } from "../../analytics/node-ids"
import { reportOperation } from "../../analytics/operation-event"
import { OPERATION_CONTRACTS } from "../../analytics/operations"
import { startDragPreview } from "../../editor-ui/drag-handle/DragPreview"
import { useHoverChrome } from "../../use-hover-chrome"
import { setCardClipboard } from "../../util/clipboard"
import { selectNodeAsDragMechanics } from "../drag-mechanics-selection/drag-mechanics-selection-extension"
import { emphasisAfterRowClick, emphasisAfterStarClick } from "./standard-extension"

// In its own file (with only component exports) so that edits hot-reload
// via React Fast Refresh instead of reloading the page.

const FOCUS_RING =
  "data-focus-visible:outline-2 data-focus-visible:outline-offset-1 data-focus-visible:outline-[rgb(23_92_211)]"

// tracked(): useHoverChrome reads this surface's verdict as a signal.
export const StandardComponent = tracked((props: ReactNodeViewProps) => {
  const { node, editor, getPos } = props
  const { standard, emphasisLevel } = node.attrs as ExtensionStandardAttrs
  const isStarred = emphasisLevel === "STARRED"
  const isExpanded = emphasisLevel !== null
  const notify = useNotifier()

  // The row is the reveal region for its own chrome (the copy/star/delete actions):
  // hover/focus + the arbiter's verdict, painted via data-chrome-visible
  // (see standard.module.css).
  const { chromeVisible, chromeProps } = useHoverChrome("standardActions")

  const statementHTML = useMemo(() => DOMPurify.sanitize(standard.statement), [standard])

  /** The standard and the card holding it, as the node view's own anchor sees them. */
  const target = (pos: number) => ({
    cardId: getContainingCardId(editor.state.doc, pos),
    standardId: standardIdOfNode(node),
  })

  const setEmphasis =
    (level: ExtensionStandardAttrs["emphasisLevel"], pos: number) =>
    ({ tr }: { tr: Transaction }) => {
      tr.setNodeAttribute(pos, "emphasisLevel", level)
      return true
    }

  const toggleExpanded = () => {
    const pos = getPos()
    if (pos === undefined) return

    const next = emphasisAfterRowClick(emphasisLevel)
    if (next === emphasisLevel) return

    editor
      .chain()
      .label(OPERATION_CONTRACTS.OP_EXPAND_STANDARD, "node-actions", { expanded: next !== null })
      .command(setEmphasis(next, pos))
      .run()
  }

  const toggleStarred = () => {
    const pos = getPos()
    if (pos === undefined) return

    const next = emphasisAfterStarClick(emphasisLevel)
    const { cardId, standardId } = target(pos)
    const chain = editor.chain()
    if (cardId && standardId) {
      chain.label(OPERATION_CONTRACTS.OP_TOGGLE_STANDARD_EMPHASIS, "node-actions", {
        cardId,
        standardId,
        emphasized: next !== null,
      })
    }
    chain.command(setEmphasis(next, pos)).run()
  }

  /** Replaces the native translucent drag ghost with a Trello-style card preview. */
  const handleDragStart = (e: DragEvent<HTMLDivElement>) => {
    const nodeDom = e.currentTarget.closest("[data-node-view-wrapper]")
    if (nodeDom instanceof HTMLElement) startDragPreview(e.nativeEvent, nodeDom)
    // Pre-select with the drag-mechanics tag; tiptap's NodeView re-selects the same
    // node later in this dragstart, which keeps the tag (see DragMechanicsSelection).
    const pos = getPos()
    if (pos !== undefined) selectNodeAsDragMechanics(editor, pos)
  }

  const copyStandard = async () => {
    const pos = getPos()
    if (pos === undefined) return
    // Read before the await: a transaction landing during the clipboard round-trip (a keystroke
    // in an earlier card, a synced remote edit) leaves `pos` inside a different card.
    const copied = target(pos)
    await setCardClipboard(editor, "standard", node)
    if (copied.cardId && copied.standardId) {
      reportOperation(editor, OPERATION_CONTRACTS.OP_COPY_STANDARD, "node-actions", {
        cardId: copied.cardId,
        standardId: copied.standardId,
      })
    }
    notify("success", "Standard Copied!", "Paste it into any card.")
  }

  const deleteStandard = () => {
    const pos = getPos()
    if (pos === undefined) return

    const { cardId, standardId } = target(pos)
    const chain = editor.chain()
    if (cardId && standardId) {
      chain.label(OPERATION_CONTRACTS.OP_DELETE_STANDARD, "node-actions", { cardId, standardId })
    }
    chain
      .command(({ tr }) => {
        tr.delete(pos, pos + node.nodeSize)
        return true
      })
      .focus()
      .run()

    notify("success", "Standard deleted!")
  }

  // The whole row toggles, except where a button inside it owns the click: the star would
  // otherwise be starred and expanded by the same press, and the code would be toggled twice.
  const handleRowClick = (e: MouseEvent<HTMLDivElement>) => {
    if (e.target instanceof Element && e.target.closest("button")) return
    toggleExpanded()
  }

  return (
    <NodeViewWrapper
      className={styles.wrapper}
      data-chrome-visible={chromeVisible || undefined}
      {...chromeProps}
    >
      <div
        className={cx(styles.standardNodeView, "standard-row", "cursor-pointer select-none")}
        data-test-standard=""
        data-emphasis-level={emphasisLevel ?? undefined}
        onClick={handleRowClick}
        // The whole row is its own drag handle: mousedown anywhere on it lets the
        // NodeView pass the ensuing dragstart to ProseMirror, which selects and drags
        // the node. (Same mechanism as DragHandle, minus the separate pad.)
        draggable={editor.isEditable}
        data-drag-handle={editor.isEditable ? "" : undefined}
        onDragStart={handleDragStart}
      >
        {/* The `{" "}` between these are the planner's gaps, not stray JSX whitespace. */}
        <AriaButton
          className={cx(
            // A `1lh`-tall flex box centers the glyph on the line box; `align-middle` on the
            // icon itself would hang it off the baseline instead.
            "inline-flex h-lh w-3 cursor-pointer items-center align-bottom",
            isStarred && "standard-star",
            FOCUS_RING,
          )}
          aria-label={isStarred ? "Unstar standard" : "Star standard"}
          // Rendered even read-only, so the row's text starts in the same place either way.
          isDisabled={!editor.isEditable}
          onPress={toggleStarred}
        >
          {isStarred ? (
            <IconRatingStarFilledIn className="size-3" />
          ) : (
            <IconRatingStarEmpty className="size-3" />
          )}
        </AriaButton>{" "}
        <AriaButton
          className={cx("standard-code inline cursor-pointer", FOCUS_RING)}
          data-test-standard-code=""
          aria-expanded={isExpanded}
          onPress={toggleExpanded}
        >
          {standard.code}
        </AriaButton>{" "}
        {/* Statements come from the standards API with embedded markup (the planner
            renders them triple-stash), so render as HTML — sanitized, since attrs
            travel through paste/drag and aren't as trustworthy as API data. */}
        <span data-test-standard-statement="" dangerouslySetInnerHTML={{ __html: statementHTML }} />{" "}
        <span className="standard-set-info" data-test-standard-set-info="">
          {standard.jurisdiction}. {standard.subject}. {standard.grades}.
        </span>
      </div>
      <div className={styles.actions} contentEditable={false}>
        <AriaButton
          className={`${styles.actionButton} ${styles.copyButton}`}
          aria-label="Copy standard"
          onPress={() => void copyStandard()}
        >
          <Copy05 />
        </AriaButton>
        {editor.isEditable && (
          <AriaButton
            className={`${styles.actionButton} ${styles.deleteButton}`}
            aria-label="Delete standard"
            onPress={deleteStandard}
          >
            <Trash01 />
          </AriaButton>
        )}
      </div>
    </NodeViewWrapper>
  )
})
